import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSelector } from "react-redux";
import {
  FaUndoAlt,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaPhoneAlt,
  FaShieldAlt,
} from "react-icons/fa";

const CancellationRefundPolicy = () => {
  const hotel = useSelector((state) => state.hotel.data);
  const hotelName = hotel?.name || "Ganesh Holiday Homes";
  const contact = hotel?.contact || {
    phone: "+91 9673150591",
    altPhone: "+91 9028139260",
    email: "ganeshholidayhomes@gmail.com",
    address: "Narayan Pakhadi Rd, Narayan Pakhadi, Shrivardhan-402110, Maharashtra, India",
  };

  const cleanPhone = (contact.phone || "+91 9673150591").replace(/[^0-9]/g, "");

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <Navbar />

      {/* Hero Banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #07101e 0%, #0b192c 50%, #1e3e62 100%)",
          color: "#ffffff",
          padding: "50px 0 60px 0",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <div className="container text-center">
          <div className="d-inline-flex align-items-center gap-2 bg-white bg-opacity-10 text-white px-3 py-1 rounded-pill small fw-semibold mb-3 border border-white border-opacity-15">
            <span>🛡️ Transparent Guest Policy • RBI & Merchant Compliant</span>
          </div>
          <h1 className="h1 fw-bold mb-2 font-heading text-white" style={{ fontSize: "2.5rem" }}>
            Cancellation & Refund Policy
          </h1>
          <p className="text-white-50 mb-0 mx-auto" style={{ maxWidth: "700px", fontSize: "1.05rem" }}>
            Clear cancellation rules, refund eligibility slabs, and bank processing timelines for {hotelName}.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="container flex-grow-1 py-5">
        <div
          className="mx-auto p-4 p-md-5 rounded-4 shadow-sm bg-white border"
          style={{ maxWidth: "900px" }}
        >
          {/* Introduction */}
          <div className="d-flex align-items-center gap-2 mb-4 text-danger">
            <FaUndoAlt className="fs-3" />
            <h2 className="h4 fw-bold mb-0 text-dark font-heading">Room Reservation Cancellation & Refund Terms</h2>
          </div>

          <p className="text-secondary mb-4" style={{ lineHeight: "1.8", fontSize: "1.02rem" }}>
            At <strong>{hotelName}</strong>, we strive to make your room booking experience completely transparent. Because we operate a boutique accommodation property with limited rooms in Shrivardhan, room reservations block inventory from other guests. Please review our official cancellation and refund terms below.
          </p>

          <hr className="my-4" />

          {/* 1. Booking Advance Deposit */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">1. Room Booking Advance Deposit</h3>
            <p className="text-secondary" style={{ lineHeight: "1.7" }}>
              A nominal booking advance token (typically <strong>₹1,000 per room</strong>) is collected to confirm and hold your room reservation. This advance is adjusted against your total accommodation bill, with the remaining balance payable upon check-in at our front desk.
            </p>
          </section>

          <hr className="my-4" />

          {/* 2. Cancellation Slabs Table */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">2. Cancellation Slabs & Refund Eligibility</h3>
            <div className="table-responsive mb-3 rounded-3 border">
              <table className="table table-striped align-middle small mb-0">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: "35%" }}>Cancellation Timing</th>
                    <th style={{ width: "35%" }}>Refund on Advance Deposit</th>
                    <th>Policy Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>7 days or more</strong> prior to check-in</td>
                    <td className="text-success fw-bold">100% Full Refund</td>
                    <td>Zero cancellation fee. Processed back to original payment source.</td>
                  </tr>
                  <tr>
                    <td><strong>3 to 6 days</strong> prior to check-in</td>
                    <td className="text-warning fw-bold text-dark">50% Refund / Free Reschedule</td>
                    <td>50% refund or 1-time free date modification (subject to room availability).</td>
                  </tr>
                  <tr>
                    <td><strong>Less than 48 hours</strong> or No-Show</td>
                    <td className="text-danger fw-bold">Non-Refundable</td>
                    <td>Advance token forfeited to cover blocked room holding costs.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <hr className="my-4" />

          {/* 3. Cancellation by Property Management */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">3. Cancellation by Property Management</h3>
            <p className="text-secondary" style={{ lineHeight: "1.7" }}>
              In the rare event that <strong>{hotelName}</strong> is unable to honor a confirmed reservation due to extreme weather, safety conditions, or emergency maintenance:
            </p>
            <ul className="list-unstyled ms-2 text-secondary">
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span><strong>100% Full Refund:</strong> Any advance amount received will be refunded immediately without any deductions.</span>
              </li>
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span><strong>Priority Assistance:</strong> We will provide alternate dates or assist you in finding suitable nearby lodging.</span>
              </li>
            </ul>
          </section>

          <hr className="my-4" />

          {/* 4. Refund Processing Timeframe */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">4. Refund Method & Processing Timeframe</h3>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="p-3 bg-light rounded-3 border h-100">
                  <div className="d-flex align-items-center gap-2 text-danger mb-1">
                    <FaClock className="fs-5" />
                    <strong className="text-dark">Initiation Window</strong>
                  </div>
                  <p className="text-secondary small mb-0">
                    Approved refunds are initiated by our accounts team within <strong>24 to 48 hours</strong> of request approval.
                  </p>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="p-3 bg-light rounded-3 border h-100">
                  <div className="d-flex align-items-center gap-2 text-success mb-1">
                    <FaShieldAlt className="fs-5" />
                    <strong className="text-dark">Bank Credit Timeline</strong>
                  </div>
                  <p className="text-secondary small mb-0">
                    The refund amount will reflect in your account within <strong>5 to 7 banking business days</strong> back to the original payment source.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <hr className="my-4" />

          {/* 5. How to Request Cancellation */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">5. How to Submit a Cancellation Request</h3>
            <p className="text-secondary mb-3">
              To request a cancellation or date change, please reach out to our front desk with your <strong>Booking Name & Check-In Date</strong>:
            </p>
            <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center pt-2">
              <a
                href={`tel:${cleanPhone}`}
                className="btn btn-outline-dark rounded-pill px-4 py-2 small fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
              >
                <FaPhoneAlt className="text-danger" /> Call: {contact.phone}
              </a>
              <a
                href={`mailto:${contact.email}?subject=Booking%20Cancellation%20Request%20-%20${hotelName}`}
                className="btn btn-outline-danger rounded-pill px-4 py-2 small fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
              >
                <FaEnvelope /> Email: {contact.email}
              </a>
            </div>
          </section>

          <hr className="my-4" />

          {/* Property Footer Note */}
          <div className="p-3 bg-light rounded-3 text-center small text-secondary">
            <p className="mb-1"><strong>{hotelName}</strong> – Standalone Short-Term Accommodation</p>
            <p className="mb-0">{contact.address}</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CancellationRefundPolicy;
